import { GemecoState } from "@gemeco/Store/GemecoState";
import updateHeaderState from "@gemeco/Store/Pages/Header/Handlers/UpdateHeaderState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Page from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import throttle from "lodash/throttle";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    homePageHeaderHeight = "homePageHeaderHeight",
    homePageHeaderScrollPosition = "homePageHeaderScrollPosition",
    regularHeaderHeight = "regularHeaderHeight",
    regularHeaderScrollPosition = "regularHeaderScrollPosition",
    headerPrintImage = "headerPrintImage",
}

const mapStateToProps = (state: GemecoState) => ({
    siteName: state.context.website.name,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    userName: state.context.session.userName,
    isSecondaryHeader: state.headerPage.isSecondaryHeader,
    location: getLocation(state),
});

const mapDispatchToProps = {
    updateHeaderState,
};

interface OwnProps extends PageProps { }

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> & {
        fields: {
            [fields.homePageHeaderHeight]: number;
            [fields.homePageHeaderScrollPosition]: number;
            [fields.regularHeaderHeight]: number;
            [fields.regularHeaderScrollPosition]: number;
            [fields.headerPrintImage]: string;
        };
    };
export interface HeaderStyles {
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerSearchContainerStyles?: InjectableCss;
    printHeaderImage?: LazyImageProps;
}

const styles: HeaderStyles = {
    websiteName: {
        css: css`
            @media print {
                display: none !important;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    headerSearchContainerStyles: {
        css: css`
            background-color: white;
        `,
    },
    printHeaderImage: {
        css: css`
            display: none;
            @media print {
                display: inline-block;
            }
        `,
    },
};

export const headerStyles = styles;

const Header: React.FC<Props> = props => {
    const { id, siteName, currentUserIsGuest, userName, isSecondaryHeader, location } = props;
    const afterHeader = React.createRef<HTMLSpanElement>();
    const isHomePage: boolean = location.pathname === "/";
    const scrollComparison = isHomePage
        ? props.fields.homePageHeaderScrollPosition
        : props.fields.regularHeaderScrollPosition;

    const scrollEvent = throttle(() => {
        const newPositionShouldBeSecondary = window.scrollY > scrollComparison;

        if (isSecondaryHeader !== newPositionShouldBeSecondary) {
            props.updateHeaderState({ isSecondaryHeader: newPositionShouldBeSecondary });
        }
    }, 250);

    React.useEffect(() => {
        document.addEventListener("scroll", scrollEvent);
        return () => document.removeEventListener("scroll", scrollEvent);
    });

    const headerHeight = isHomePage ? props.fields.homePageHeaderHeight : props.fields.regularHeaderHeight;

    let headerPageStyle = {
        css: css`
            background-color: #000000;
            border-bottom: ${isHomePage ? "none" : "1px solid #d6d6d6"};
            max-width: none !important;
            z-index: 999;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            position: fixed;
                            top: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    };
    if (isSecondaryHeader) {
        headerPageStyle = {
            css: css`
                background-color: #000000;
                max-width: none !important;
                z-index: 999;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                border-bottom: ${isHomePage ? "none" : "1px solid #d6d6d6"};
                            `,
                            null,
                            null,
                            css`
                                position: fixed;
                                top: 0;
                                border-bottom: none;
                            `,
                        ],
                        "min",
                    )}
            `,
        };
    }

    const stickyHeaderPlaceholderStyle = {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            height: ${headerHeight}px;
                        `,
                    ],
                    "min",
                )}
        `,
    };

    return (
        <>
            <StyledWrapper {...stickyHeaderPlaceholderStyle}></StyledWrapper>
            <LazyImage {...styles.printHeaderImage} src={props.fields.headerPrintImage} />
            <Page className="header" as="header" isHeader={true} padding={0} {...headerPageStyle}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <Hidden below="lg">
                    <Zone contentId={id} zoneName="TopInfoBar" />
                    <Zone contentId={id} zoneName="LogoAndLinks" />
                </Hidden>
                <Hidden above="md">
                    <Zone contentId={id} zoneName="MobileNavigation" />
                </Hidden>
                <StyledWrapper {...styles.headerSearchContainerStyles}>
                    <Zone contentId={id} zoneName="HeaderSearch" />
                </StyledWrapper>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Header),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
        fieldDefinitions: [
            {
                name: fields.homePageHeaderHeight,
                editorTemplate: "IntegerField",
                displayName: "Home Page Header: Height",
                defaultValue: 136,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.homePageHeaderScrollPosition,
                editorTemplate: "IntegerField",
                displayName: "Home Page Header: Scroll Position Toggle",
                defaultValue: 250,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.regularHeaderHeight,
                editorTemplate: "IntegerField",
                displayName: "Page Header: Height",
                defaultValue: 232,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.regularHeaderScrollPosition,
                editorTemplate: "IntegerField",
                displayName: "Page Header: Scroll Position Toggle",
                defaultValue: 0,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.headerPrintImage,
                displayName: "Header Print Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default pageModule;
